// frontend/src/components/licensing/LicenseGraceBanner.js
//
// Reads X-License-Grace-Expiry and X-License-Days-Remaining headers injected
// by the backend LicenseEnforcementMiddleware and shows a dismissible warning.
// Mount this once inside ModernMainLayout (or App.js) at the top of the page.

import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

const STORAGE_KEY = 'license_grace_banner_dismissed';

// The API interceptor in services/api.js stores grace headers on window for
// easy access without threading them through props / context.
// See: apps/licensing/middleware.py → _add_grace_headers()

function readGraceFromWindow() {
  return {
    graceExpiry: window.__licenseGraceExpiry || null,
    daysRemaining: window.__licenseDaysRemaining != null
      ? parseInt(window.__licenseDaysRemaining, 10)
      : null,
  };
}

export default function LicenseGraceBanner() {
  const [visible, setVisible] = useState(false);
  const [graceExpiry, setGraceExpiry] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    // Check on mount and after the first API call has had a chance to populate
    const timer = setTimeout(() => {
      const { graceExpiry: exp, daysRemaining: days } = readGraceFromWindow();
      if (!exp) return;

      const dismissed = sessionStorage.getItem(STORAGE_KEY);
      if (dismissed === exp) return; // already dismissed for this expiry date

      setGraceExpiry(exp);
      setDaysRemaining(days);
      setVisible(true);
    }, 1000); // slight delay to let first API response set window vars

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    if (graceExpiry) {
      sessionStorage.setItem(STORAGE_KEY, graceExpiry);
    }
  };

  if (!visible) return null;

  const daysText = daysRemaining != null
    ? `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} overdue`
    : '';

  return (
    <Alert
      severity="warning"
      icon={<WarningAmber />}
      action={
        <Button color="inherit" size="small" onClick={handleDismiss}>
          Dismiss
        </Button>
      }
      sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1200 }}
    >
      <AlertTitle>License Grace Period</AlertTitle>
      Your license expired on <strong>{graceExpiry}</strong>
      {daysText && ` (${daysText})`}.
      {' '}Access continues during the grace period — please renew to avoid interruption.{' '}
      <a href="mailto:sales@biztimize.com" style={{ color: 'inherit', fontWeight: 600 }}>
        Contact sales
      </a>
    </Alert>
  );
}
